import { Tooltip } from '@material-ui/core';
import { cloneDeep, isEmpty } from 'lodash-es';
import React, { useEffect, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { Link } from 'react-router-dom';

import { highlightedDivStyle, spacing } from '~/src/dash/components/layout/common';
import { IItemIDNameRoute } from '~/src/dash/components/tables/IDNameTable/common';
import { ListViewTable } from '~/src/dash/components/tables/ListViewTable';
import {
  getComparator,
  ICustomRenderCellJSXProps,
  IListViewTableColumnInfo,
  IListViewTableColumns,
  IListViewTableRow,
  Order,
  RowsPerPage,
  stableSort,
} from '~/src/dash/components/tables/ListViewTable/common';
import { useListViewTableStore } from '~/src/dash/components/tables/ListViewTable/context';
import { Col, Cols, Row, Rows, Text, TextInput } from '~/src/dash/publicaUI';
import { getItemRoute, IItemRouteID, ItemRouteType } from '~/src/dash/router/routes';
import { LoadingStateHasData } from '~/src/dash/utils/loading-states';
import { useBaseStore } from '~/src/dash/views/base/contexts';

const CustomNameRender = (props: ICustomRenderCellJSXProps) => {
  const { value } = props;
  const { customProps } = useListViewTableStore();
  const { session, user } = useBaseStore();
  return (
    <Link
      to={`${getItemRoute(customProps.type, props.row.ID as IItemRouteID, session, user)}`}
      style={{
        color: 'inherit',
        textDecoration: 'inherit',
      }}
      target='_blank'
    >
      <Tooltip title={value} placement='top-start'>
        <div>
          <Text
            small
            bold
            style={{
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
            }}
          >
            {value}
          </Text>
        </div>
      </Tooltip>
    </Link>
  );
};

const defaultColumns: IListViewTableColumns = {
  ID: {
    formatValue: (value: number) => value.toString(),
    isHidden: true,
    isSearchable: true,
    key: 'ID',
    label: 'ID',
    requestKey: 'ID',
    responseKey: 'ID',
    valueType: 'string',
  },
  Name: {
    cellJSX: CustomNameRender,
    defaultOrderDirection: 'asc',
    headerRenderType: 'name',
    isSearchable: true,
    key: 'Name',
    label: 'Name',
    requestKey: 'name',
    responseKey: 'Name',
    valueType: 'string',
    orderByKey: 'Name',
  },
};

export const formatDataIntoLVTRows = (
  data: Record<string, any>[],
  columns: IListViewTableColumns,
): IListViewTableRow[] => {
  return data.map((datum) => {
    const formattedRow = {} as IListViewTableRow;
    Object.values(columns).forEach((col) => {
      const { cellJSX, cellRenderType, cellStyle, formatValue, isNumeric, key, responseKey } = col;
      let value: any = datum[responseKey];
      if (isNumeric) {
        value = value !== null ? Number(value) : null;
      }
      if (formatValue) {
        value = formatValue(value);
      }
      formattedRow[key] = {
        value,
        ...(cellJSX ? { jsx: cellJSX } : {}),
        ...(cellRenderType ? { renderType: cellRenderType } : {}),
        ...(cellStyle ? { cellStyle } : {}),
        ...(datum?.ParentID ? { parentID: datum.ParentID } : {}),
      };
    });
    return formattedRow;
  });
};

const queryData = (data: IItemIDNameRoute[], query: string) => {
  let queriedRows = cloneDeep(data);
  if (query !== '') {
    queriedRows = data.filter((row) => {
      for (const key of ['ID', 'Name']) {
        // @ts-ignore
        if (row[key].toString().toLowerCase().includes(query.toLowerCase())) {
          return true;
        }
      }
      return false;
    });
  }

  return queriedRows;
};

interface IProps {
  title: string;
  subTitle: string;
  cypressClassName?: string;
  customFunctions?: Record<string, any>;
  data: IItemIDNameRoute[];
  type: ItemRouteType;
}

export const IDNameTable = ({
  title,
  subTitle,
  cypressClassName,
  customFunctions,
  data,
  type,
}: IProps) => {
  const [queriedData, setQueriedData] = useState<IItemIDNameRoute[]>(data);
  const [totalRowCount, setTotalRowCount] = useState(0);

  const [query, setQuery] = useState('');

  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<string>('Name');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [sortedRows, setSortedRows] = useState<IListViewTableRow[]>([]);

  useEffect(() => {
    const queriedRows = queryData(data, query);
    unstable_batchedUpdates(() => {
      setQueriedData(queriedRows);
      setTotalRowCount(queriedRows.length);
      setPage(0);
    });
  }, [data, query]);

  useEffect(() => {
    if (!isEmpty(queriedData)) {
      setSortedRows(
        stableSort(rows, getComparator(order, orderBy, defaultColumns[orderBy].valueType)),
      );
    } else {
      setSortedRows([]);
    }
  }, [order, orderBy, queriedData]);

  const handleOnClickColumnHeader = (column: IListViewTableColumnInfo) => {
    const { orderByKey, requestKey, defaultOrderDirection } = column;
    const key = orderByKey || requestKey;

    if (orderBy !== key) {
      unstable_batchedUpdates(() => {
        setOrder(defaultOrderDirection || 'asc');
        setOrderBy(key);
        setPage(0);
      });
    } else {
      const isAsc = orderBy === key && order === 'asc';
      unstable_batchedUpdates(() => {
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(key);
        setPage(0);
      });
    }
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = Number(event.target.value) as RowsPerPage;
    unstable_batchedUpdates(() => {
      setRowsPerPage(newRowsPerPage);
      setPage(0);
    });
  };

  const handleChangePage = (_e: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleOnChangeQuery = (text: string) => {
    setQuery(text);
  };

  const rows = formatDataIntoLVTRows(queriedData, defaultColumns);
  const visibleRows = sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Rows basePadding gap={spacing.base} style={highlightedDivStyle}>
      <Row noHorizontalPadding smallVerticalPadding>
        <Cols style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Col flex>
            <Text large bold>
              {title}
            </Text>
            {subTitle && <Text small>{subTitle}</Text>}
          </Col>
          <Col>
            <TextInput
              enableClear
              handleOnChange={handleOnChangeQuery}
              msDelay={500}
              placeholder='Search by Name or ID'
              prefix='search'
            />
          </Col>
        </Cols>
      </Row>
      <Row noPadding>
        <ListViewTable
          className={`id-name-table-${cypressClassName}`}
          loadState={LoadingStateHasData}
          activeColumnKeys={[]}
          displayNameWithID
          columns={defaultColumns}
          customFunctions={customFunctions}
          order={order}
          orderBy={orderBy}
          page={page}
          rows={visibleRows}
          rowsPerPage={rowsPerPage}
          totalRowCount={totalRowCount}
          handleOnClickColumnHeader={handleOnClickColumnHeader}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          isExpanded={false}
          isReadAuthorized={true}
          isWriteAuthorized={true}
          key={subTitle}
          customProps={{ type }}
        />
      </Row>
    </Rows>
  );
};
